import React, { useState, useEffect, useCallback } from 'react';
import { Navigation } from '../components/Navigation';
import { Newsletter } from '../components/Newsletter';
import { NetWorthComparison } from '../components/NetWorthComparison';
import { motion } from 'framer-motion';
import { Calculator, Plus, Trash2, Save, FileSpreadsheet, File as FilePdf, Building2, Car, Briefcase, Wallet, CreditCard, Home, DollarSign, Check, AlertCircle } from 'lucide-react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import toast from 'react-hot-toast';

interface Entry {
  id: string;
  name: string;
  amount: number;
  category: string;
  type: 'asset' | 'liability';
  customCategory?: boolean;
}

const defaultAssetCategories = [
  { value: 'real-estate', label: 'Real Estate', icon: Home },
  { value: 'vehicles', label: 'Vehicles', icon: Car },
  { value: 'investments', label: 'Investments', icon: Briefcase },
  { value: 'cash', label: 'Cash & Bank Accounts', icon: Wallet },
  { value: 'other-assets', label: 'Other Assets', icon: DollarSign },
];

const defaultLiabilityCategories = [
  { value: 'mortgages', label: 'Mortgages', icon: Building2 },
  { value: 'loans', label: 'Loans', icon: DollarSign },
  { value: 'credit-cards', label: 'Credit Cards', icon: CreditCard },
  { value: 'other-liabilities', label: 'Other Liabilities', icon: DollarSign },
];

const NetWorthCalculator = () => {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [newCustomCategory, setNewCustomCategory] = useState('');
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [location, setLocation] = useState<string>('Northeast');
  const [age, setAge] = useState<number>(30);
  const [monthlyIncome, setMonthlyIncome] = useState<number>(0);

  useEffect(() => {
    const savedEntries = localStorage.getItem('netWorthEntries');
    const savedCategories = localStorage.getItem('customCategories');
    
    if (savedEntries) {
      setEntries(JSON.parse(savedEntries));
    }
    if (savedCategories) {
      setCustomCategories(JSON.parse(savedCategories));
    }
  }, []);

  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      handleSave();
    }, 30000);

    return () => clearInterval(autoSaveInterval);
  }, [entries, customCategories]);

  const handleSave = useCallback(() => {
    setIsSaving(true);
    try {
      localStorage.setItem('netWorthEntries', JSON.stringify(entries));
      localStorage.setItem('customCategories', JSON.stringify(customCategories));
      setLastSaved(new Date());
      toast.success('Data saved successfully');
    } catch (error) {
      toast.error('Failed to save data');
    } finally {
      setIsSaving(false);
    }
  }, [entries, customCategories]);

  const addEntry = (type: 'asset' | 'liability') => {
    const newEntry: Entry = {
      id: Math.random().toString(36).substr(2, 9),
      name: '',
      amount: 0,
      category: type === 'asset' ? 'other-assets' : 'other-liabilities',
      type
    };
    setEntries([...entries, newEntry]);
  };

  const updateEntry = (id: string, field: keyof Entry, value: any) => {
    setEntries(entries.map(entry => {
      if (entry.id === id) {
        return { ...entry, [field]: value };
      }
      return entry;
    }));
  };

  const removeEntry = (id: string) => {
    setEntries(entries.filter(entry => entry.id !== id));
  };

  const addCustomCategory = () => {
    if (newCustomCategory && !customCategories.includes(newCustomCategory)) {
      setCustomCategories([...customCategories, newCustomCategory]);
      setNewCustomCategory('');
    }
  };

  const calculateTotals = () => {
    const totals = entries.reduce(
      (acc, entry) => {
        if (entry.type === 'asset') {
          acc.assets += entry.amount;
        } else {
          acc.liabilities += entry.amount;
        }
        return acc;
      },
      { assets: 0, liabilities: 0 }
    );
    
    return {
      ...totals,
      netWorth: totals.assets - totals.liabilities
    };
  };

  const exportToCSV = () => {
    const totals = calculateTotals();
    const headers = ['Type', 'Category', 'Name', 'Amount'];
    const rows = entries.map(entry => [
      entry.type,
      entry.category,
      entry.name,
      entry.amount.toFixed(2)
    ]);
    
    rows.push(['', '', 'Total Assets', totals.assets.toFixed(2)]);
    rows.push(['', '', 'Total Liabilities', totals.liabilities.toFixed(2)]);
    rows.push(['', '', 'Net Worth', totals.netWorth.toFixed(2)]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `net-worth-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV exported successfully');
  };

  const exportToPDF = () => {
    const totals = calculateTotals();
    const doc = new jsPDF();
    
    doc.setFontSize(20);
    doc.text('Net Worth Summary', 14, 20);
    
    doc.setFontSize(12);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);
    
    doc.text('Summary:', 14, 45);
    doc.text(`Total Assets: $${totals.assets.toLocaleString()}`, 20, 55);
    doc.text(`Total Liabilities: $${totals.liabilities.toLocaleString()}`, 20, 65);
    doc.text(`Net Worth: $${totals.netWorth.toLocaleString()}`, 20, 75);
    
    const assetData = entries
      .filter(entry => entry.type === 'asset')
      .map(entry => [
        entry.category,
        entry.name,
        `$${entry.amount.toLocaleString()}`
      ]);
    
    doc.text('Assets:', 14, 90);
    (doc as any).autoTable({
      head: [['Category', 'Name', 'Amount']],
      body: assetData,
      startY: 95,
      theme: 'grid',
      styles: { fontSize: 10 }
    });
    
    const liabilityData = entries
      .filter(entry => entry.type === 'liability')
      .map(entry => [
        entry.category,
        entry.name,
        `$${entry.amount.toLocaleString()}`
      ]);
    
    doc.text('Liabilities:', 14, (doc as any).lastAutoTable.finalY + 15);
    (doc as any).autoTable({
      head: [['Category', 'Name', 'Amount']],
      body: liabilityData,
      startY: (doc as any).lastAutoTable.finalY + 20,
      theme: 'grid',
      styles: { fontSize: 10 }
    });
    
    doc.save(`net-worth-${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF exported successfully');
  };

  const totals = calculateTotals();

  return (
    <div className="min-h-screen bg-[var(--background)] p-6">
      <div className="max-w-7xl mx-auto">
        <Navigation />

        <div className="mb-8">
          <Newsletter 
            title="Track Your Wealth Journey"
            subtitle="Get expert tips on building and tracking your net worth, plus wealth management strategies."
          />
        </div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="glass-card p-8 mb-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-emerald-500/10">
              <Calculator className="w-6 h-6 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-semibold">Net Worth Calculator</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="glass-card p-6 border border-emerald-500/20">
              <h3 className="text-lg font-semibold text-emerald-500 mb-2">Total Assets</h3>
              <p className="text-3xl font-bold">${totals.assets.toLocaleString()}</p>
            </div>
            <div className="glass-card p-6 border border-red-500/20">
              <h3 className="text-lg font-semibold text-red-500 mb-2">Total Liabilities</h3>
              <p className="text-3xl font-bold">${totals.liabilities.toLocaleString()}</p>
            </div>
            <div className="glass-card p-6 border border-emerald-500/20">
              <h3 className="text-lg font-semibold text-emerald-500 mb-2">Net Worth</h3>
              <p className={`text-3xl font-bold ${totals.netWorth >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                ${totals.netWorth.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mb-8">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => addEntry('asset')}
              className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Asset
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => addEntry('liability')}
              className="px-4 py-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Liability
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    <Save className="w-4 h-4" />
                  </motion.div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save
                </>
              )}
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={exportToCSV}
              className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Export CSV
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={exportToPDF}
              className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
            >
              <FilePdf className="w-4 h-4" />
              Export PDF
            </motion.button>

            {lastSaved && (
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Check className="w-4 h-4" />
                Last saved: {lastSaved.toLocaleTimeString()}
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm text-slate-400 mb-2">Add Custom Category</label>
                <input
                  type="text"
                  value={newCustomCategory}
                  onChange={(e) => setNewCustomCategory(e.target.value)}
                  className="glass-input px-4 py-2 w-full rounded-lg"
                  placeholder="Enter category name"
                />
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={addCustomCategory}
                className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors"
              >
                Add Category
              </motion.button>
            </div>

            <div className="glass-card p-6 border border-emerald-500/20">
              <h3 className="text-xl font-semibold text-emerald-500 mb-4">Assets</h3>
              <div className="space-y-4">
                {entries
                  .filter(entry => entry.type === 'asset')
                  .map(entry => (
                    <div key={entry.id} className="glass-card p-4 hover:bg-slate-800/30 transition-colors">
                      <div className="grid md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm text-slate-400 mb-2">Category</label>
                          <select
                            value={entry.category}
                            onChange={(e) => updateEntry(entry.id, 'category', e.target.value)}
                            className="glass-input px-4 py-2 w-full rounded-lg"
                          >
                            {defaultAssetCategories.map(cat => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                            {customCategories.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm text-slate-400 mb-2">Name</label>
                          <input
                            type="text"
                            value={entry.name}
                            onChange={(e) => updateEntry(entry.id, 'name', e.target.value)}
                            className="glass-input px-4 py-2 w-full rounded-lg"
                            placeholder="Asset name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-slate-400 mb-2">Amount</label>
                          <div className="relative">
                            <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                            <input
                              type="number"
                              value={entry.amount}
                              onChange={(e) => updateEntry(entry.id, 'amount', Number(e.target.value))}
                              className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={() => removeEntry(entry.id)}
                            className="px-4 py-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors w-full flex items-center justify-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            <div className="glass-card p-6 border border-red-500/20">
              <h3 className="text-xl font-semibold text-red-500 mb-4">Liabilities</h3>
              <div className="space-y-4">
                {entries
                  .filter(entry => entry.type === 'liability')
                  .map(entry => (
                    <div key={entry.id} className="glass-card p-4 hover:bg-slate-800/30 transition-colors">
                      <div className="grid md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm text-slate-400 mb-2">Category</label>
                          <select
                            value={entry.category}
                            onChange={(e) => updateEntry(entry.id, 'category', e.target.value)}
                            className="glass-input px-4 py-2 w-full rounded-lg"
                          >
                            {defaultLiabilityCategories.map(cat => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                            {customCategories.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm text-slate-400 mb-2">Name</label>
                          <input
                            type="text"
                            value={entry.name}
                            onChange={(e) => updateEntry(entry.id, 'name', e.target.value)}
                            className="glass-input px-4 py-2 w-full rounded-lg"
                            placeholder="Liability name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-slate-400 mb-2">Amount</label>
                          <div className="relative">
                            <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                            <input
                              type="number"
                              value={entry.amount}
                              onChange={(e) => updateEntry(entry.id, 'amount', Number(e.target.value))}
                              className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={() => removeEntry(entry.id)}
                            className="px-4 py-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors w-full flex items-center justify-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-8">
          <div className="glass-card p-6">
            <h3 className="text-xl font-semibold mb-6">Net Worth Comparison</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="space-y-2">
                <label className="block text-sm text-slate-400">Age</label>
                <input
                  type="number"
                  value={age}
                  onChange={(e) => setAge(Number(e.target.value))}
                  className="glass-input px-4 py-2 w-full rounded-lg"
                  min="18"
                  max="100"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm text-slate-400">Region</label>
                <select
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="glass-input px-4 py-2 w-full rounded-lg"
                >
                  <option value="Northeast">Northeast</option>
                  <option value="Midwest">Midwest</option>
                  <option value="South">South</option>
                  <option value="West">West</option>
                  <option value="Alaska/Hawaii">Alaska/Hawaii</option>
                </select>
              </div>
            </div>
            
            <NetWorthComparison
              age={age}
              income={monthlyIncome * 12}
              netWorth={totals.netWorth}
              location={location}
            />
          </div>
        </div>

        <div className="mt-8">
          <Newsletter 
            title="Build Your Wealth Smarter"
            subtitle="Subscribe for personalized wealth-building strategies and financial planning tips."
          />
        </div>
      </div>
    </div>
  );
};

export default NetWorthCalculator;